import { useDroppable } from '@dnd-kit/core';
import { useState, useEffect, useRef } from 'react';
import KanbanCard from './KanbanCard';
import {
  MoreHorizontal,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  ArrowRight,
  X,
  Check
} from 'lucide-react';

export default function KanbanColumn({
  columnId,
  title,
  leads,
  onRename,
  onDelete,
  onMove
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: String(columnId),
    disabled: false
  });

  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(title);

  const menuRef = useRef(null);

  /* =========================
     SINCRONIZAR TÍTULO
  ========================= */
  useEffect(() => {
    setNewTitle(title);
  }, [title]);

  /* =========================
     CERRAR MENÚ AL CLICK FUERA
  ========================= */
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const validLeads = (leads || []).filter(l => l && l.id);

  /* =========================
     ACCIONES
  ========================= */
  const startEdit = () => {
    setEditing(true);
    setMenuOpen(false);
  };

  const cancelEdit = () => {
    setEditing(false);
    setNewTitle(title);
  };

  const saveEdit = () => {
    if (onRename && newTitle.trim() && newTitle !== title) {
      onRename(columnId, newTitle.trim());
    }
    setEditing(false);
  };

  const confirmDelete = () => {
    setMenuOpen(false);
    const ok = window.confirm(
      '¿Eliminar esta columna?\nLos leads NO se eliminarán.'
    );
    if (ok && onDelete) {
      onDelete(columnId);
    }
  };

  const move = (direction) => {
    setMenuOpen(false);
    onMove?.(columnId, direction);
  };

  return (
    <div
      ref={setNodeRef}
      className={`
        w-72 flex-shrink-0 rounded-[2rem] transition-all duration-300 self-start
        ${isOver ? 'bg-blue-50/80 ring-2 ring-blue-200 shadow-sm' : 'bg-slate-50/50'}
      `}
    >
      {/* ================= HEADER ================= */}
      <div className="p-4 flex items-center justify-between relative">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />

          {/* TÍTULO */}
          {editing ? (
            <input
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              className="text-[10px] font-bold uppercase tracking-[0.1em]
                         text-slate-700 bg-white border rounded px-2 py-1 w-full"
              autoFocus
            />
          ) : (
            <h2 className="font-bold text-[10px] uppercase tracking-[0.1em]
                           text-slate-500 truncate">
              {title}
            </h2>
          )}

          <span className="bg-slate-200/60 text-slate-600 px-1.5 py-0.5
                           rounded-lg text-[9px] font-black">
            {validLeads.length}
          </span>
        </div>

        {/* BOTÓN MENÚ */}
        <button
          onClick={() => setMenuOpen(v => !v)}
          className="text-slate-300 hover:text-slate-900 transition-colors p-1"
        >
          <MoreHorizontal size={14} />
        </button>

        {/* ================= MENÚ ================= */}
        {menuOpen && !editing && (
          <div
            ref={menuRef}
            className="absolute top-12 right-4 bg-white border
                       rounded-lg shadow-md text-xs z-20 w-44"
          >
            <button
              onClick={startEdit}
              className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50"
            >
              <Pencil size={12} />
              Renombrar
            </button>

            <button
              onClick={() => move('left')}
              className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50"
            >
              <ArrowLeft size={12} />
              Mover a la izquierda
            </button>

            <button
              onClick={() => move('right')}
              className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50"
            >
              <ArrowRight size={12} />
              Mover a la derecha
            </button>

            <div className="border-t my-1" />

            <button
              onClick={confirmDelete}
              className="flex items-center gap-2 w-full px-3 py-2
                         text-red-600 hover:bg-red-50"
            >
              <Trash2 size={12} />
              Eliminar columna
            </button>
          </div>
        )}
      </div>

      {/* ================= BOTONES EDICIÓN ================= */}
      {editing && (
        <div className="px-4 pb-2 flex gap-3 text-xs">
          <button
            onClick={saveEdit}
            className="flex items-center gap-1 text-green-600 hover:text-green-700"
          >
            <Check size={12} />
            Guardar
          </button>
          <button
            onClick={cancelEdit}
            className="flex items-center gap-1 text-red-500 hover:text-red-600"
          >
            <X size={12} />
            Cancelar
          </button>
        </div>
      )}

      {/* ================= CARDS ================= */}
      <div className="px-3 pb-5 space-y-2.5">
        {validLeads.length === 0 ? (
          <div
            className={`
              h-24 border-2 border-dashed rounded-[1.5rem]
              flex flex-col items-center justify-center
              ${isOver ? 'border-blue-300 bg-blue-100/30' : 'border-slate-200'}
            `}
          >
            <div className="w-7 h-7 rounded-full bg-slate-100
                            flex items-center justify-center mb-2">
              <Plus size={12} className="text-slate-400" />
            </div>
            <p className="text-[9px] font-black uppercase tracking-tighter text-slate-400">
              Vacío
            </p>
          </div>
        ) : (
          validLeads.map(lead => (
            <KanbanCard key={lead.id} lead={lead} />
          ))
        )}
      </div>
    </div>
  );
}
